import { useRef, useState, type FormEvent } from 'react'
import { useDialog } from './useDialog'
import { api, ApiError } from '../api'
import { Field, Notice } from './ui'
import { pluralize } from '../format'
import { durationToken } from '../finance/motion'

/**
 * Смена собственного пароля.
 *
 * Текущий пароль спрашивается обязательно: без него любой, кто добрался
 * до незаблокированного экрана, меняет пароль и запирает владельца.
 */
export function ChangePasswordModal({ onClose: onClosed, returnFocus }: { onClose: () => void; returnFocus?: () => HTMLElement | null }) {
  const dialog = useRef<HTMLDivElement>(null)
  // Окно гаснет (--dur-leave) и только потом уходит из разметки.
  const [closing, setClosing] = useState(false)
  const onClose = () => {
    if (closing) return
    setClosing(true)
    window.setTimeout(onClosed, durationToken('--dur-leave', 200))
  }
  // Escape, Tab по кругу внутри окна, фокус в первое поле и обратно —
  // на имя пользователя или «Ещё»: пункт меню, с которого открыли, уже закрыт.
  useDialog(dialog, { active: true, onClose, returnFocus })
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)

    if (next !== repeat) {
      setError('Новый пароль и подтверждение не совпадают')
      return
    }

    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    try {
      const result = await api.changePassword(current, next)
      setDone(
        result.other_sessions_revoked > 0
          ? `Пароль изменён. Завершено ${pluralize(result.other_sessions_revoked, 'другая сессия', 'другие сессии', 'других сессий')}.`
          : 'Пароль изменён.',
      )
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось сменить пароль')
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <div className={`modal-backdrop${closing ? ' modal-backdrop--leave' : ''}`} onClick={onClose}>
      <div
        ref={dialog}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="modal__title" id="change-password-title">Смена пароля</h2>

        {done ? (
          <div className="stack" style={{ gap: 14 }}>
            <Notice tone="info">{done}</Notice>
            <button className="btn btn--primary" onClick={onClose} style={{ width: '100%' }}>
              Закрыть
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="stack" style={{ gap: 13 }}>
            <Field label="Текущий пароль">
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                required
              />
            </Field>

            <Field label="Новый пароль" hint="Не короче 8 символов">
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={next}
                onChange={(event) => setNext(event.target.value)}
                required
              />
            </Field>

            <Field label="Повторите новый пароль">
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
                required
              />
            </Field>

            {error && <Notice tone="bad">{error}</Notice>}

            <div className="tiny muted">
              После смены пароля все остальные ваши сессии будут завершены. Текущая останется активной.
            </div>

            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={onClose}>
                Отмена
              </button>
              <button type="submit" className="btn btn--primary" disabled={busy}>
                {busy ? 'Меняем…' : 'Сменить пароль'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
